import { CameraIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import {
    useGetScreenshotAttemptsByContentIdQuery,
    ScreenshotAttempt
} from '../../api/client'
import ScreenshotAttemptList from './ScreenshotAttemptList'
import ScreenshotDetailsModal from './ScreenshotDetailsModal'
import ScreenshotImageModal from './ScreenshotImageModal'

type Props = {
    contentId: number
}

/**
 * Inline panel displaying screenshot attempt history for a content item.
 * Same content as ContentAttemptsModal but rendered as a card rather than a modal.
 */
export default function ScreenshotAttemptsPanel({ contentId }: Props) {
    const [selectedScreenshot, setSelectedScreenshot] = useState<ScreenshotAttempt | null>(null)
    const [detailsModalOpen, setDetailsModalOpen] = useState(false)
    const [imageModalOpen, setImageModalOpen] = useState(false)

    const { data, loading, error } = useGetScreenshotAttemptsByContentIdQuery({
        variables: { contentId },
        skip: !contentId
    })

    const attempts = data?.getScreenshotAttemptsByContentId || []

    return (
        <>
            <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-4">
                    <CameraIcon className="h-6 w-6 text-gray-700" />
                    <h2 className="text-lg font-semibold text-gray-900">Screenshot attempts</h2>
                    {attempts.length > 0 && (
                        <span className="text-sm text-gray-500">({attempts.length})</span>
                    )}
                </div>
                <div className="px-6 py-4">
                    <ScreenshotAttemptList
                        attempts={attempts}
                        loading={loading}
                        error={error}
                        onViewImage={(attempt) => {
                            setSelectedScreenshot(attempt)
                            setImageModalOpen(true)
                        }}
                        onViewLogs={(attempt) => {
                            setSelectedScreenshot(attempt)
                            setDetailsModalOpen(true)
                        }}
                    />
                </div>
            </div>

            <ScreenshotDetailsModal
                open={detailsModalOpen}
                setOpen={setDetailsModalOpen}
                screenshot={selectedScreenshot}
            />
            <ScreenshotImageModal
                open={imageModalOpen}
                setOpen={setImageModalOpen}
                imageUrl={selectedScreenshot?.s3Url || null}
                contentId={contentId}
            />
        </>
    )
}
